import { z } from 'zod';
import { GameId } from './game.ts';
import { Odds, GameSelection } from './market.ts';
import { Money, SignedMoney } from './money.ts';
import { BetId, CommandId, GameRoundId, PartnerWalletRoundId, PromotionId, UtcInstant } from './primitives.ts';
import { FreebetPolicy, LightningTier } from './promotion.ts';

/** Server-side bet lifecycle. DRAFT exists only in the client and is not part of this enum. */
export const BetState = z.enum([
  'SUBMITTED',
  'DEBIT_PENDING',
  'ACCEPTED',
  'REJECTED',
  'COMPENSATION_PENDING',
  'COMPENSATED',
  'RESULT_WON',
  'RESULT_LOST',
  'CREDIT_PENDING',
  'SETTLED',
  'REFUND_PENDING',
  'VOIDED',
]);
export type BetState = z.infer<typeof BetState>;

export const BetRejectReason = z.enum([
  'BETTING_CLOSED',
  'INSUFFICIENT_FUNDS',
  'SESSION_EXPIRED',
  'INVALID_STAKE',
  'CURRENCY_MISMATCH',
  'QUOTE_CHANGED',
  'LIABILITY_LIMIT',
  'SOURCE_UNAVAILABLE',
  'IDEMPOTENCY_CONFLICT',
  'DEBIT_UNRESOLVED_AT_CLOSE',
  'STAKE_LIMIT',
  'REWARD_UNAVAILABLE',
  'PROMO_NOT_STACKABLE',
  'WALLET_UNAVAILABLE',
]);
export type BetRejectReason = z.infer<typeof BetRejectReason>;

/** Snapshot of the price at acceptance; never re-priced afterwards. */
export const AcceptedQuote = z.object({
  gameRoundId: GameRoundId,
  selection: GameSelection,
  odds: Odds,
  oddsVersion: z.string().min(1),
  lightning: Odds.nullable(),
  lightningTier: LightningTier.nullable(),
  boostBps: z.number().int().nonnegative(),
  payoutCap: Money.nullable(),
  /** PromoMathPolicy version the payout is computed with. */
  promoPolicyVersion: z.string().min(1),
  freebetPolicy: FreebetPolicy.nullable(),
  quotedAt: UtcInstant,
});
export type AcceptedQuote = z.infer<typeof AcceptedQuote>;

export const PlaceBetCommand = z.object({
  commandId: CommandId,
  gameRoundId: GameRoundId,
  selection: GameSelection,
  stake: Money,
  expectedOddsVersion: z.string().min(1),
  /** Inventory item (freebet or boost) to use on this bet. */
  rewardId: PromotionId.nullable().optional(),
});
export type PlaceBetCommand = z.infer<typeof PlaceBetCommand>;

export const Bet = z.object({
  betId: BetId,
  commandId: CommandId,
  gameId: GameId,
  gameRoundId: GameRoundId,
  partnerWalletRoundId: PartnerWalletRoundId.nullable(),
  selection: GameSelection,
  /** Stake at risk. For a freebet this is the face value and `cashDebit` is zero. */
  stake: Money,
  funding: z.enum(['CASH', 'FREEBET']),
  cashDebit: Money,
  rewardId: PromotionId.nullable(),
  quote: AcceptedQuote.nullable(),
  state: BetState,
  rejectReason: BetRejectReason.nullable(),
  /** Amount credited to the wallet (cash bets: total return incl. stake; PROFIT_ONLY freebets: profit). Net is separate. */
  payoutGross: Money.nullable(),
  payoutNet: SignedMoney.nullable(),
  submittedAt: UtcInstant,
  updatedAt: UtcInstant,
});
export type Bet = z.infer<typeof Bet>;
